import { Injectable } from '@angular/core';
import { BehaviorSubject, map, mergeMap, of, tap } from 'rxjs';
import { CoreItemType } from '../core-item/core-item';
import { CoreItemService } from '../core-item/core-item.service';
import { WorkspaceHolderService } from '../workspace/workspace.holder.service';
import { LambdaService } from './../../shared/core/lambda.service';
import { Chat, ChatMessage, ChatSubdata } from './chat';

@Injectable({
  providedIn: 'root',
})
export class ChatService {
  constructor(
    private coreItemService: CoreItemService,
    private lambdaService: LambdaService,
    private workspaceHolderService: WorkspaceHolderService
  ) {}

  private _changeSubject = new BehaviorSubject<Chat | undefined>(undefined);

  getAllChatList$(parentId?: string) {
    if (!parentId) {
      return of([] as Chat[]);
    }
    const coreItemList$ = this.coreItemService.getCoreItemList$({
      coreTypeList: [CoreItemType.chat],
      parentIdList: [parentId],
    });

    return this._changeSubject.asObservable().pipe(
      mergeMap((_) => {
        return coreItemList$;
      })
    );
  }

  getChat$(id: string) {
    return this.coreItemService.getItemByAction$({ id }).pipe(
      mergeMap((data) => {
        // Load all chat messages
        return this.coreItemService
          .getCoreItemList$({
            coreTypeList: [CoreItemType.chatMessage],
            parentIdList: [id],
          })
          .pipe(
            map((chatMessageList) => {
              return {
                chat: data as Chat,
                chatMessageList: (chatMessageList as ChatMessage[]).filter(
                  (c) => c.coreType === CoreItemType.chatMessage && c.parentId === id
                ),
              };
            })
          );
      })
    );
  }

  updateChat$(chat?: Chat) {
    if (!chat) {
      return of({});
    }
    return this.coreItemService.updateCoreItem$(chat).pipe(
      map((c) => {
        this._changeSubject.next(c as Chat);
        return c as Chat;
      })
    );
  }

  addChat$(title: string, chatSubData: ChatSubdata, parentId: string) {
    return this.coreItemService
      .batchWriteMetadataAndRelationList$({
        addMetadataList: [
          {
            title,
            coreType: CoreItemType.chat,
            subData: chatSubData,
            parentId,
          },
        ],
      })
      .pipe(
        map((response) => {
          this._changeSubject.next(response[0] as Chat);
          return response[0];
        })
      );
  }

  deleteChat$(chat?: Chat) {
    if (!chat) {
      return of({});
    }

    const deleteMetadataList: { id: string; sk: string }[] = [{ id: chat.id, sk: chat.sk }];

    return this.getChat$(chat.id).pipe(
      mergeMap((response) => {
        deleteMetadataList.push(...response.chatMessageList.map((c) => ({ id: c.id, sk: c.sk })));
        return this.coreItemService
          .batchWriteMetadataAndRelationList$({
            deleteMetadataList,
          })
          .pipe(
            tap((response) => {
              this._changeSubject.next(undefined);
            })
          );
      })
    );
  }
}
